// Resource.Element — content-tree primitives.
//
// Introduced in Phase 6 of the resource-model migration. `Content`
// is still a branded string in the domain (the JSON serialisation
// lives at the shell boundary), but Elements are the *semantic*
// shape the string represents: a tree of typed nodes with stable
// ids, optional text leaves and optional child arrays.
//
// **Element ids** are strings; the shell assigns them at parse or
// author time. Materials use whatever the agent (or editor) injects;
// sources use **content-derived** ids from `paragraphSplit` below
// (`p-<fnv1a-hex>`) — edit-stable, so a citation survives inserts /
// reorders of other sections (see the `paragraphSplit` header).
export { mkElementId, paragraphSplit, parseContentTree, rankElementMatches, serializeContentTree, };
import { Maybe } from "@bilda/utils";
import * as Content from "./content.js";
const mkElementId = (s) => s;
// Sentinel for nodes whose `id` was missing at the parse boundary.
// Named (not an inline `""`) so the "unlabelled node" case is
// searchable and never mistaken for an accidental empty string.
const emptyElementId = mkElementId("");
// === Parse ===
//
// Content is stored as a JSON-serialised string. This helper
// parses it into an Element tree; malformed input returns `none`
// so callers can degrade gracefully (grounding falls back to
// word-overlap when the tree isn't parseable).
//
// Missing `id` fields are tolerated at the parse boundary and
// filled with an empty-brand marker — that means references can't
// point at unlabelled nodes, but the tree is still walkable for
// word-overlap. `parseContentTree` never throws.
const parseContentTree = (content) => {
    let raw;
    try {
        raw = JSON.parse(content);
    }
    catch {
        return Maybe.nothing;
    }
    if (!Array.isArray(raw))
        return Maybe.nothing;
    const parsed = [];
    for (const item of raw) {
        const el = toElement(item);
        if (el === undefined)
            return Maybe.nothing;
        parsed.push(el);
    }
    return Maybe.just(parsed);
};
const toElement = (raw) => {
    if (typeof raw !== "object" || raw === null)
        return undefined;
    if (!("type" in raw) || typeof raw.type !== "string")
        return undefined;
    const id = "id" in raw && typeof raw.id === "string"
        ? mkElementId(raw.id)
        : emptyElementId;
    const el = { id, type: raw.type };
    if ("text" in raw && typeof raw.text === "string") {
        el.text = raw.text;
    }
    if ("children" in raw && Array.isArray(raw.children)) {
        const kids = [];
        for (const c of raw.children) {
            const k = toElement(c);
            if (k === undefined)
                return undefined;
            kids.push(k);
        }
        el.children = kids;
    }
    // Legacy `refs` field is deliberately IGNORED (Phase 7d) —
    // citations now live in KB. `migrateInlineRefsToKb` reads the
    // raw JSON directly for backfill. Old materials still parse
    // (refs are just dropped on the floor).
    // Copy any surface-level primitive attrs (correct, level, etc.)
    // that aren't part of the core shape into `attrs` — the shell
    // per-template validators decide what to do with them.
    const attrs = {};
    // `serializeContentTree` re-emits the bag under an explicit `attrs`
    // key, so a parse↔serialize round-trip feeds that key straight back
    // in here. Adopt it AS the bag (not as one more surface key) — else
    // each cycle would nest `attrs` one level deeper (`attrs.attrs.…`),
    // breaking serialize's own documented "stable across serialise /
    // parse / re-serialise cycles" invariant and the 8a freshness hash.
    if ("attrs" in raw &&
        typeof raw.attrs === "object" &&
        raw.attrs !== null &&
        !Array.isArray(raw.attrs)) {
        for (const [key, val] of Object.entries(raw.attrs))
            attrs[key] = val;
    }
    // Object.entries gives typed (key, value) pairs off the narrowed
    // `object`, so no cast is needed to read the value — the previous
    // `raw as Record<...>` laundered unknown values past the type
    // system at exactly this untrusted-JSON boundary. `attrs` is skipped
    // here (adopted above); a bare inline extra (a hand-seeded `correct`)
    // still lands in the same bag, so both shapes parse identically.
    for (const [key, val] of Object.entries(raw)) {
        if (key === "id" ||
            key === "type" ||
            key === "text" ||
            key === "children" ||
            key === "refs" ||
            key === "attrs")
            continue;
        attrs[key] = val;
    }
    if (Object.keys(attrs).length > 0)
        el.attrs = attrs;
    return el;
};
// === paragraphSplit ===
//
// The source-side structural projection: split plain text on blank-line
// boundaries into addressable paragraph sections. Each section id is
// **content-derived** (`p-<fnv1a-hex>`), NOT position-based — so a
// section survives inserts / reorders / deletes of OTHER sections. That
// edit-stability is the whole point: a citation to paragraph "p-ab12cd34"
// stays valid when a paragraph is added above it, where the old `p{i}`
// position ids drifted (everything after an inserted paragraph
// renumbered, silently breaking every citation past the edit point). A
// section's id changes only when its OWN text changes — which correctly
// flags a citation to the now-edited text as needing review.
//
// These ids are the `ElementSegment` values a `Anchor.StructuralSelector`
// addresses (`anchor.ts`): a reference path is a structural selector over
// content-stable section ids. The projection stays a pure recompute over
// the extract (a cache, not event-truth) — deterministic, so nothing
// needs to persist it.
//
// Ids are dot-free (the `.`-joined citation-locator invariant holds) and
// occurrence-suffixed (`-1`, `-2`, …) so duplicate paragraphs still get
// distinct, deterministic ids. Edge: inserting an IDENTICAL duplicate
// shifts later duplicates' occurrence suffix — rare, and unique
// paragraphs (the common case) are fully stable.
//
// Post-MVP: real Markdown / PDF-outline parsing preserving heading /
// page structure (the `Anchor.PageSegment` / `HeadingSegment` variants).
// fnv1a — a small pure non-crypto string hash (32-bit, hex). Pure
// arithmetic only (no crypto / I/O), so it belongs in the domain: the
// content-address of a paragraph is a deterministic function of its text.
const fnv1a = (s) => {
    let h = 0x811c9dc5;
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return (h >>> 0).toString(16).padStart(8, "0");
};
const paragraphSplit = (text) => {
    const trimmed = text.trim();
    if (trimmed.length === 0)
        return [];
    const parts = trimmed.split(/\n\s*\n/);
    const seen = new Map();
    return parts.map((chunk) => {
        const body = chunk.trim();
        const base = fnv1a(body);
        const n = seen.get(base) ?? 0;
        seen.set(base, n + 1);
        const id = n === 0 ? `p-${base}` : `p-${base}-${n}`;
        return { id: mkElementId(id), type: "paragraph", text: body };
    });
};
// === serializeContentTree ===
//
// Inverse of `parseContentTree`. Reassembles the JSON payload the
// shell stores. Key order is fixed (`id`, `type`, `text`, `children`,
// `attrs`) so hashing the serialised bytes is stable across
// serialise / parse / re-serialise cycles — otherwise the 8a
// freshness invariant would trip on cosmetic diffs. Used by the
// 8b edit-primitive pipeline: App workflow simulates a primitive
// via `applyEdit`, serialises the result, hashes it, dispatches;
// evolve replays the same steps.
const serializeContentTree = (tree) => Content.mk(JSON.stringify(tree.map(elementToJson)));
const elementToJson = (el) => {
    const out = { id: el.id, type: el.type };
    if (el.text !== undefined)
        out.text = el.text;
    if (el.children !== undefined)
        out.children = el.children.map(elementToJson);
    if (el.attrs !== undefined)
        out.attrs = el.attrs;
    return out;
};
const W_TYPE_EXACT = 4;
const W_TYPE_INFERRED = 2;
const W_ORDINAL = 1.5;
const W_TOKEN_OVERLAP = 3;
const W_TYPE_TOKEN = 1;
const MIN_SCORE = 0.5;
const SNIPPET_MAX = 120;
const rankElementMatches = (tree, query) => {
    const descTokens = tokens(query.description);
    if (descTokens.size === 0 && query.typeHint === undefined)
        return [];
    const inferred = inferredType(descTokens);
    const candidates = [];
    let encounter = 0;
    visitTree(tree, [], (el, path, ordinalWithinType) => {
        encounter++;
        if (el.id.length === 0)
            return; // unaddressable — see docs above
        const elText = collectText(el);
        const elTokens = tokens(elText);
        let score = 0;
        if (query.typeHint !== undefined && query.typeHint === el.type) {
            score += W_TYPE_EXACT;
        }
        if (inferred !== undefined && inferred === el.type) {
            score += W_TYPE_INFERRED;
        }
        if (matchesOrdinal(descTokens, ordinalWithinType)) {
            score += W_ORDINAL;
        }
        score += W_TOKEN_OVERLAP * jaccard(descTokens, elTokens);
        if (descTokens.has(el.type))
            score += W_TYPE_TOKEN;
        if (score < MIN_SCORE)
            return;
        candidates.push({
            path,
            type: el.type,
            score,
            snippet: truncateSnippet(elText),
            encounterOrder: encounter,
        });
    });
    candidates.sort((a, b) => {
        if (b.score !== a.score)
            return b.score - a.score;
        return a.encounterOrder - b.encounterOrder;
    });
    const limit = Math.max(0, Math.min(query.limit, candidates.length));
    return candidates.slice(0, limit).map(({ path, type, score, snippet }) => ({
        path,
        type,
        score,
        snippet,
    }));
};
// BFS traversal that yields (el, path, ordinalWithinType). The
// ordinal counts same-type siblings encountered so far under the
// same parent; used by `matchesOrdinal` to answer "was this the
// third question?".
const visitTree = (siblings, parentPath, visit) => {
    const typeCounts = new Map();
    for (const el of siblings) {
        const nextOrdinal = (typeCounts.get(el.type) ?? 0) + 1;
        typeCounts.set(el.type, nextOrdinal);
        const path = [...parentPath, el.id];
        visit(el, path, nextOrdinal);
        if (el.children !== undefined) {
            visitTree(el.children, path, visit);
        }
    }
};
// === Text helpers ===
const STOPWORDS = new Set([
    // English MVP set
    "a",
    "an",
    "and",
    "are",
    "as",
    "at",
    "be",
    "but",
    "by",
    "for",
    "from",
    "has",
    "have",
    "in",
    "is",
    "it",
    "its",
    "of",
    "on",
    "or",
    "that",
    "the",
    "this",
    "to",
    "was",
    "were",
    "with",
    // Swedish MVP set
    "att",
    "av",
    "de",
    "den",
    "det",
    "en",
    "ett",
    "för",
    "har",
    "i",
    "inte",
    "med",
    "och",
    "på",
    "som",
    "till",
    "var",
    "vid",
    "är",
]);
const PUNCT_RE = /[\p{P}\p{S}]/gu;
// Lowercase, strip Unicode punctuation + symbols, split on whitespace,
// drop stopwords + empty tokens. Returns a set so the jaccard math is
// straightforward. Idempotent + total.
const tokens = (s) => {
    const cleaned = s.toLowerCase().replace(PUNCT_RE, " ");
    const parts = cleaned.split(/\s+/);
    const out = new Set();
    for (const t of parts) {
        if (t.length === 0)
            continue;
        if (STOPWORDS.has(t))
            continue;
        out.add(t);
    }
    return out;
};
const jaccard = (a, b) => {
    if (a.size === 0 && b.size === 0)
        return 0;
    let intersection = 0;
    for (const x of a)
        if (b.has(x))
            intersection++;
    const union = a.size + b.size - intersection;
    if (union === 0)
        return 0;
    return intersection / union;
};
// leaf → text; branch → space-joined descendant text.
const collectText = (el) => {
    if (el.text !== undefined)
        return el.text;
    if (el.children === undefined)
        return "";
    return el.children.map(collectText).join(" ");
};
const truncateSnippet = (s) => {
    const trimmed = s.trim().replace(/\s+/g, " ");
    if (trimmed.length <= SNIPPET_MAX)
        return trimmed;
    return `${trimmed.slice(0, SNIPPET_MAX - 1)}…`;
};
// === Type + ordinal inference ===
const TYPE_LEXICON = {
    // English
    question: "question",
    questions: "question",
    option: "option",
    options: "option",
    answer: "option",
    answers: "option",
    choice: "option",
    choices: "option",
    paragraph: "paragraph",
    paragraphs: "paragraph",
    heading: "heading",
    headings: "heading",
    stem: "stem",
    // Swedish
    fråga: "question",
    frågor: "question",
    frågan: "question",
    svar: "option",
    svaret: "option",
    alternativ: "option",
    alternativet: "option",
    val: "option",
    stycke: "paragraph",
    stycken: "paragraph",
    rubrik: "heading",
    rubriker: "heading",
};
const inferredType = (descTokens) => {
    for (const t of descTokens) {
        const hit = TYPE_LEXICON[t];
        if (hit !== undefined)
            return hit;
    }
    return undefined;
};
// Ordinal words → numeric position (1-indexed). Both English and
// Swedish MVP set. Numeric forms handled separately via regex.
const ORDINAL_WORDS = {
    first: 1,
    second: 2,
    third: 3,
    fourth: 4,
    fifth: 5,
    sixth: 6,
    seventh: 7,
    eighth: 8,
    ninth: 9,
    tenth: 10,
    första: 1,
    andra: 2,
    tredje: 3,
    fjärde: 4,
    femte: 5,
    sjätte: 6,
    sjunde: 7,
    åttonde: 8,
    nionde: 9,
    tionde: 10,
};
const ORDINAL_NUMERIC_RE = /^(\d+)(?:st|nd|rd|th)?$/;
// True if `descTokens` names an ordinal equal to `elOrdinal`. Ordinal
// can appear as a word ("second") or numeric ("2", "2nd").
const matchesOrdinal = (descTokens, elOrdinal) => {
    for (const t of descTokens) {
        const word = ORDINAL_WORDS[t];
        if (word !== undefined && word === elOrdinal)
            return true;
        const match = ORDINAL_NUMERIC_RE.exec(t);
        if (match !== null) {
            const seg = match[1];
            if (seg === undefined)
                continue;
            const n = Number.parseInt(seg, 10);
            if (n === elOrdinal)
                return true;
        }
    }
    return false;
};
//# sourceMappingURL=element.js.map